import React, { createContext, useContext, useState, useEffect, ReactNode }from 'react';
import { authService }from '../services/auth.service';
import { User }from '@shared/types';
import { STORAGE_KEYS } from '@shared/constants';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ssoToken = params.get('token');

      if (ssoToken && ssoToken.includes('.')) {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, ssoToken);
        params.delete('token');
        const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, '', cleanUrl);
      }

      const isAuth = await authService.isAuthenticated();
      if (!isAuth) {
        setUser(null);
        return;
      }

      const bootstrappedUser = await authService.bootstrapFromToken();
      setUser(bootstrappedUser);
    }catch {
      // Không log chi tiết auth error để hạn chế lộ thông tin
      setUser(null);
    }finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const result = await authService.login({ email, password });
    setUser(result.user);
  };

  const logout = async () => {
    try {
      await authService.logout();
    }finally {
      setUser(null);
    }
  };

  const refreshUser = async () => {
    const refreshedUser = await authService.refreshStoredPermissions();
    if (refreshedUser) setUser(refreshedUser);
  };

  useEffect(() => {
    const handleFocus = () => void refreshUser();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
